import React, { useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../../../css/Views/HelpArticleView.css";
import { helpArticles } from "./HelpArticles";

/**
 * Stabilny slug (PL znaki + duże litery)
 */
const slugify = (input) => {
  return String(input ?? "")
    .toLowerCase()
    .replace(/[ąĄ]/g, "a")
    .replace(/[ćĆ]/g, "c")
    .replace(/[ęĘ]/g, "e")
    .replace(/[łŁ]/g, "l")
    .replace(/[ńŃ]/g, "n")
    .replace(/[óÓ]/g, "o")
    .replace(/[śŚ]/g, "s")
    .replace(/[żŻ]/g, "z")
    .replace(/[źŹ]/g, "z")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
};

const extractText = (children) => {
  if (children == null) return "";
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(extractText).join("");
  if (typeof children === "object" && children.props?.children)
    return extractText(children.props.children);
  return "";
};

const HelpArticleView = () => {
  const { articleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const article = useMemo(
    () => helpArticles.find((a) => a.id === articleId),
    [articleId]
  );

  useEffect(() => {
    if (!article) return;
    if (!location.hash) return;

    const id = decodeURIComponent(location.hash.substring(1));

    const t = setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);

    return () => clearTimeout(t);
  }, [location.hash, articleId, article]);

  return (
    <div className="bank-transfers-view">
      <div className="bank-header">Poradnik systemowy</div>

      {!article ? (
        <div className="help-empty">
          Nie znaleziono artykułu: {articleId}
        </div>
      ) : (
        <div className="help-article-card">
          <div className="markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children, ...props }) => {
                  const isHashLink =
                    typeof href === "string" && href.startsWith("#");

                  if (!isHashLink) {
                    return (
                      <a href={href} {...props}>
                        {children}
                      </a>
                    );
                  }

                  return (
                    <a
                      href={href}
                      {...props}
                      onClick={(e) => {
                        e.preventDefault();

                        const id = decodeURIComponent(href.slice(1));
                        const element = document.getElementById(id);

                        if (element) {
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }

                        navigate(
                          { hash: `#${encodeURIComponent(id)}` },
                          { replace: true }
                        );
                      }}
                    >
                      {children}
                    </a>
                  );
                },

                h1: ({ children, ...props }) => {
                  const text = extractText(children);
                  const id = slugify(text);
                  return (
                    <h1 id={id} {...props}>
                      {children}
                    </h1>
                  );
                },

                h2: ({ children, ...props }) => {
                  const text = extractText(children);
                  const id = slugify(text);
                  return (
                    <h2 id={id} {...props}>
                      {children}
                    </h2>
                  );
                },

                h3: ({ children, ...props }) => {
                  const text = extractText(children);
                  const id = slugify(text);
                  return (
                    <h3 id={id} {...props}>
                      {children}
                    </h3>
                  );
                },

                h4: ({ children, ...props }) => {
                  const text = extractText(children);
                  const id = slugify(text);
                  return (
                    <h4 id={id} {...props}>
                      {children}
                    </h4>
                  );
                },
              }}
            >
              {article.content}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpArticleView;